import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import '../styles/Activities.css';

/* ── Images ── */
import img_mountain  from '../assets/image/mountain_village.png';

/* ── Données ── */
const API_URL = `${process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000/api'}/activites/`;

/* ══════════════════════════════════════
   Carrousel — Défilement natif (style téléphone)
══════════════════════════════════════ */
function PhotoCarousel({ photos, titre, isModal }) {
  const [current, setCurrent] = useState(0);
  const scrollRef = useRef(null);

  const safePhotos = photos && photos.length > 0 ? photos : [img_mountain];

  const handleScroll = () => {
    if (scrollRef.current) {
      const scrollPos = scrollRef.current.scrollLeft;
      const width = scrollRef.current.clientWidth;
      const newCurrent = Math.round(scrollPos / width);
      if (newCurrent !== current) {
        setCurrent(newCurrent);
      }
    }
  };

  const scrollTo = (index) => {
    if (scrollRef.current) {
      const width = scrollRef.current.clientWidth;
      scrollRef.current.scrollTo({ left: width * index, behavior: 'smooth' });
    }
  };

  const goNext = (e) => {
    e.stopPropagation();
    scrollTo(current === safePhotos.length - 1 ? 0 : current + 1);
  };

  const goPrev = (e) => {
    e.stopPropagation();
    scrollTo(current === 0 ? safePhotos.length - 1 : current - 1);
  };

  return (
    <div className="act-carousel">
      <div 
        className="act-carousel-track-native" 
        ref={scrollRef}
        onScroll={handleScroll}
      >
        {safePhotos.map((photo, i) => (
          <img
            key={i}
            src={photo}
            alt={`${titre} — ${i + 1}`}
            className="act-carousel-img-native"
          />
        ))}
      </div>

      {safePhotos.length > 1 && (
        <>
          <button
            className="act-carousel-btn act-carousel-prev"
            onClick={goPrev}
            aria-label="Précédent"
          >‹</button>
          <button
            className="act-carousel-btn act-carousel-next"
            onClick={goNext}
            aria-label="Suivant"
          >›</button>
          <div className="act-carousel-dots">
            {safePhotos.map((_, i) => (
              <button
                key={i}
                className={`act-carousel-dot${i === current ? ' active' : ''}`}
                onClick={(e) => { e.stopPropagation(); scrollTo(i); }}
                aria-label={`Photo ${i + 1}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}


/* ══════════════════════════════════════
   Carte activité
══════════════════════════════════════ */
function ActivityCard({ activity }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const style = { bg: 'rgba(245,197,10,0.15)', color: '#b45309' };
  
  const handleClick = () => {
    navigate(`/activites/${activity.id}`);
  };

  return (
    <div className="act-card" onClick={handleClick} role="button" tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && handleClick()}>
      <PhotoCarousel photos={activity.photos} titre={activity.titre} isModal={false} />
      <div className="act-card-body">
        <span className="act-category-badge" style={{ background: style.bg, color: style.color }}>
          {activity.categorie.charAt(0).toUpperCase() + activity.categorie.slice(1)}
        </span>
        <h3 className="act-card-title">{activity.titre}</h3>
        <p className="act-card-desc">{activity.description}</p>
        <span className="act-card-hint">{t('activities.actHint')}</span>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════
   Composant principal
══════════════════════════════════════ */
function ActivitiesList() {
  const { t, i18n } = useTranslation();
  const [activities, setActivities] = useState([]);
  const [activeFilter, setActiveFilter] = useState('ALL');

  useEffect(() => {
    const fetchActivities = async () => {
      try {
        const res = await fetch(API_URL, {
          headers: {
            'Accept-Language': i18n.language || 'fr'
          }
        });
        if (res.ok) {
          const data = await res.json();
          const formatted = data.map(act => ({
            ...act,
            photos: act.photos && act.photos.length > 0 
              ? act.photos.map(p => p.image) 
              : [img_mountain] 
          }));
          setActivities(formatted);
        }
      } catch (err) {
        console.error(t('activities.errFetch'), err);
      }
    };
    fetchActivities();
  }, [i18n.language, t]);

  const filtered = activeFilter === 'ALL'
    ? activities
    : activities.filter((a) => a.categorie.toLowerCase() === activeFilter.toLowerCase());

  const uniqueCats = [...new Set(activities.map(a => a.categorie ? a.categorie.charAt(0).toUpperCase() + a.categorie.slice(1).toLowerCase() : ''))].filter(Boolean);
  const dynamicCategories = ['ALL', ...uniqueCats];

  return (
    <section className="act-section">
      <div className="act-container">

        {/* ── En-tête + Définition ── */}
        <div className="act-definition-header">
          <span className="act-definition-label">{t('activities.listBadge')}</span>
          <h2 className="act-definition-title">{t('activities.listTitle1')}<span className="keyword">{t('activities.listTitleKeyword1')}</span>{t('activities.listTitle2')}<span className="keyword">{t('activities.listTitleKeyword2')}</span></h2>
        </div>
        {/* ── Bloc combiné (Définition + CTA Gîte) ── */}
        <div className="act-combined-banner">
          <div className="act-combined-col">
            <div className="act-combined-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <circle cx="12" cy="12" r="10" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M12 16v-4M12 8h.01" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <div className="act-combined-text">
              <span className="act-combined-label">{t('activities.assocLabel')}</span>
              <p><strong>TIWIZI</strong> — {t('activities.assocText')}</p>
            </div>
          </div>

          <div className="act-combined-divider"></div>

          <div className="act-combined-col">
            <div className="act-combined-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" strokeLinecap="round" strokeLinejoin="round" />
                <polyline points="9 22 9 12 15 12 15 22" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <div className="act-combined-text">
              <span className="act-combined-label">{t('activities.ctaLabel')}</span>
              <p>{t('activities.ctaText')}</p>
              <a href="/reservation" className="btn-primary">{t('activities.btnBook')}</a>
            </div>
          </div>
        </div>

        {/* ── Filtres ── */}
        <div className="act-filters">
          {dynamicCategories.map((cat) => (
            <button
              key={cat}
              className={`act-filter-btn${activeFilter.toLowerCase() === cat.toLowerCase() ? ' active' : ''}`}
              onClick={() => setActiveFilter(cat)}
            >
              {cat === 'ALL' ? t('activities.filterAll') : cat}
            </button>
          ))}
        </div>

        {/* ── Grille ── */}
        <div className="act-grid">
          {filtered.length === 0 ? (
            <div className="act-empty">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" />
                <path d="M12 8V12" />
                <path d="M12 16H12.01" />
              </svg>
              <p>{activities.length === 0 ? t('activities.empty') : t('activities.emptyFilter')}</p>
            </div>
          ) : (
            filtered.map((activity) => (
              <ActivityCard
                key={activity.id}
                activity={activity}
              />
            ))
          )}
        </div>



      </div>
    </section>
  );
}

export default ActivitiesList;
